import {
  AccessibleOutlined,
  HomeOutlined,
  LocalHospitalOutlined,
  Person,
  Phone,
  WcOutlined,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import MessageDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import {
  fetchPatient,
  resetCurrentPatient,
  updatePatient,
} from '../../redux/patient/patientAction';
import { fetchWards } from '../../redux/ward/wardAction';

function PatientForm() {
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    title: 'Notification',
    message: '',
    type: 'success',
  });

  const methods = useForm({
    defaultValues: {
      ip_no: '',
      address: '',
      patient_Name: '',
      phone: '',
      status: '',
      gender: '',
      dob: '',
      department: '',
      block: '',
      ward: '',
      floor: '',
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = methods;

  const watchBlock = watch('block');
  const watchWard = watch('ward');
  const watchFloor = watch('floor');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const departmentList = useSelector(
    (state) => state?.department?.fetchDepartments || {}
  );
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const patientStates = useSelector(
    (state) => state?.patients?.fetchPatient || {}
  );
  const patientUpdate = useSelector(
    (state) => state?.patients?.updatePatient || {}
  );

  const patientData = patientStates?.data?.data || null;

  useEffect(() => {
    dispatch(fetchPatient(id));
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
  }, [dispatch, id]);

  useEffect(() => {
    if (patientStates?.success && patientData) {
      setValue('ip_no', patientData.ip_number || '');
      setValue('address', patientData.address || '');
      setValue('patient_Name', patientData.patient_name || '');
      setValue('phone', patientData.phone || '');
      setValue('status', patientData.User?.status || '');
      setValue('age', patientData.age || '');

      if (patientData.dob) {
        const date = new Date(patientData.dob);
        const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        setValue('dob', formattedDate);
      }

      setValue(
        'gender',
        patientData.gender_type === 'M'
          ? 'Male'
          : patientData.gender_type === 'F'
          ? 'Female'
          : 'Other'
      );

      if (patientData.department_id && patientData.Department) {
        setValue('department', patientData.department_id);
      }

      if (patientData.block_id && patientData.Block) {
        setValue('block', patientData.block_id);
      }

      if (patientData.ward_id && patientData.Ward) {
        setValue('ward', patientData.ward_id);
      }

      if (patientData.floor_id && patientData.Floor) {
        setValue('floor', patientData.floor_id);
      }
    }
  }, [patientData, patientStates?.success, setValue]);

  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchWards(watchBlock, watchFloor));
    }
  }, [watchBlock, watchFloor, dispatch]);

  useEffect(() => {
    if (watchBlock && watchWard) {
      dispatch(fetchFloors(watchBlock, watchWard));
    }
  }, [watchBlock, watchWard, dispatch]);

  useEffect(() => {
    if (patientUpdate.success) {
      reset();
      setMessageDialog({
        open: true,
        title: 'Patient Update',
        message: 'Patient updated successfully!',
        type: 'success',
      });
      dispatch(resetCurrentPatient());
    } else if (patientUpdate.error) {
      if (patientUpdate.errorFields) {
        Object.entries(patientUpdate.errorFields).forEach(
          ([field, message]) => {
            setError(field, {
              type: 'server',
              message: message,
            });
          }
        );
      } else if (
        typeof patientUpdate.error === 'string' &&
        patientUpdate.error.includes('phone must be unique')
      ) {
        setError('phone', {
          type: 'server',
          message: 'Phone number already exists in the system',
        });
      } else {
        const errorMessage =
          patientUpdate.errorMessage ||
          patientUpdate.error ||
          'An error occurred while submitting the form';
        setMessageDialog({
          open: true,
          title: 'Update Error',
          message: errorMessage,
          type: 'error',
        });
      }
      dispatch(resetCurrentPatient());
    }
  }, [patientUpdate, reset, setError, dispatch]);

  const inputFields = useMemo(
    () => [
      {
        name: 'ip_no',
        label: 'IP Number',
        placeholder: 'Enter IP Number',
        type: 'text',
        tabIndex: 1,
        rules: { required: 'IP Number is required' },
        startIcon: <LocalHospitalOutlined />,
      },
      {
        name: 'address',
        label: 'Address',
        placeholder: 'Enter address',
        type: 'text',
        tabIndex: 3,
        rules: { required: 'Address is required' },
        startIcon: <HomeOutlined />,
      },
      {
        name: 'age',
        label: 'Age',
        tabIndex: 5,
        placeholder: 'Enter date of birth',
        type: 'text',
        rules: {
          required: 'Age is required',
          pattern: {
            value: /^[0-9]+$/,
            message: 'Please enter a valid age (numbers only)',
          },
        },
        startIcon: <AccessibleOutlined />,
      },
      {
        name: 'patient_Name',
        label: 'Patient Name',
        placeholder: 'Enter patient name',
        type: 'text',
        tabIndex: 2,
        rules: { required: 'Patient Name is required' },
        startIcon: <Person />,
      },
      {
        name: 'gender',
        label: 'Gender',
        placeholder: 'Select gender',
        type: 'select',
        tabIndex: 4,
        defaultValue: patientData?.gender_type || '',
        options: [
          { value: 'Male', label: 'Male' },
          { value: 'Female', label: 'Female' },
          { value: 'Other', label: 'Other' },
        ],
        rules: { required: 'Gender is required' },
        startIcon: <WcOutlined />,
      },
      {
        name: 'phone',
        label: 'Phone Number',
        placeholder: 'Enter phone number',
        type: 'tel',
        tabIndex: 6,
        maxLength: 10,
        showCharCount: false,
        rules: {
          // required: "Phone number is required",
          pattern: {
            value: /^(6|7|8|9)\d{9}$/,
            message: 'Invalid phone number',
          },
          maxLength: {
            value: 10,
            message: 'Phone number should be 10 digits',
          },
        },
        startIcon: <Phone />,
      },
      // {
      //   name: "status",
      //   label: "Status",
      //   placeholder: "Select status",
      //   // type: "select",
      //   readOnly:true,
      //   // defaultValue: patientData?.User?.status || "",
      //   // options: [
      //   //   { value: 1, label: "Active" },
      //   //   { value: 2, label: "Inactive" },
      //   // ],
      //   // rules: { required: "Status is required" },
      //   startIcon: <AccessibleOutlined />,
      // },
    ],
    [departmentList, blockList, wardList, floorList, patientData]
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`;
  };

  const onSubmit = (data) => {
    clearErrors();

    const patientData = {
      patient_name: data.patient_Name,
      ip_number: data.ip_no,
      phone: data.phone,
      address: data.address || '',
      gender_type:
        data.gender === 'Male' ? 'M' : data.gender === 'Female' ? 'F' : 'O',
      status: data.status || '1',
      age: data.age,
    };

    dispatch(updatePatient(id, patientData));
  };

  const handleCancel = () => {
    reset();
    clearErrors();
  };

  const handleBack = () => {
    navigate(APP_LINK.LISTALLPATIENT);
  };

  const handleDialogClose = () => {
    setMessageDialog({ ...messageDialog, open: false });

    // If it was a success message, navigate back after closing
    if (messageDialog.type === 'success') {
      navigate(-1);
    }
  };

  return (
    <Box sx={styles.container}>
      <FormProvider {...methods}>
        <Paper sx={styles.paper} elevation={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={8} lg={9} xl={11}>
              <Typography variant="h3" sx={styles.heading}>
                Edit In Patient Registration
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={3} xl={1}>
              <Button
                variant="contained"
                color="error"
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <ReusableFormFields
                  inputFields={inputFields}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  gridProps={{ xs: 12, md: 6, xl: 6 }}
                  control={control}
                />
              </Grid>
            </Grid>

            <Box sx={styles.buttonsContainer}>
              <CustomButton
                type="button"
                text="Cancel"
                variant="contained"
                disableElevation
                size="small"
                onClick={handleCancel}
              />
              <CustomButton
                type="submit"
                text="Submit"
                variant="contained"
                disableElevation
                size="small"
              />
            </Box>
          </form>
        </Paper>

        {/* Using the common MessageDialog component */}
        <MessageDialog
          open={messageDialog.open}
          onClose={handleDialogClose}
          title={messageDialog.title}
          message={messageDialog.message}
          type={messageDialog.type}
        />
      </FormProvider>
    </Box>
  );
}

export default PatientForm;
